import { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config';
import DataTable from 'react-data-table-component';
import { 
    Eye, Calendar, Receipt, DollarSign, ShoppingBag, 
    X, CreditCard, Banknote, Loader2, TrendingUp, Hash, Printer, Ban, Search, FileSpreadsheet
} from 'lucide-react';
import { useTableSort } from '../hooks/useTableSort';
import SortableTh from '../components/SortableTh';
import { formatFechaHoraLista, rangoHoyChile } from '../utils/fechasChile';

/**
 * VentasPage - Sales history with detailed view
 * Shows all sales with ability to see line-item details
 */
export default function VentasPage() {
    const [ventas, setVentas] = useState([]);
    const [summary, setSummary] = useState(null);
    const [searching, setSearching] = useState(false);
    const [exporting, setExporting] = useState(false);
    // Range of the last search: the list, totals and Excel export always use it.
    const [searched, setSearched] = useState(null);
    const [selectedVenta, setSelectedVenta] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const rangoInicial = rangoHoyChile();
    const [fechaDesde, setFechaDesde] = useState(rangoInicial.desde);
    const [fechaHasta, setFechaHasta] = useState(rangoInicial.hasta);
    const [printingDay, setPrintingDay] = useState(false);

    const MAX_PRINT_DAYS = 31;

    const token = localStorage.getItem('token');
    const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };

    // Only the small daily/monthly summary loads on open; sales load when the user presses Buscar.
    useEffect(() => {
        fetchSummary();
    }, []);

    const datePart = (value) => String(value || '').slice(0, 10);

    const daysInRange = (desde, hasta) => {
        const a = datePart(desde);
        const b = datePart(hasta);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(a) || !/^\d{4}-\d{2}-\d{2}$/.test(b)) return 0;
        const d0 = new Date(`${a}T12:00:00`);
        const d1 = new Date(`${b}T12:00:00`);
        return Math.floor((d1 - d0) / (24 * 60 * 60 * 1000)) + 1;
    };

    const formatFiltroLabel = (value) => {
        const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
        if (!m) return value || '';
        const fecha = `${m[3]}-${m[2]}-${m[1]}`;
        return m[4] ? `${fecha} ${m[4]}:${m[5]}` : fecha;
    };

    const displayVendedor = (row) =>
        row.vendedor_piso_name || row.vendedor_name || 'Sin asignar';

    const ventaAnulada = (row) => row.estado === false || row.estado === 'false';

    const anularVenta = async (row) => {
        const ok = window.confirm(
            `¿Anular la venta #${row.id}?\n\nÚselo si se imprimió el ticket y el cliente no pudo pagar. Solo funciona con el turno de caja todavía abierto.`
        );
        if (!ok) return;
        try {
            const res = await fetch(`${API_URL}/api/sales/${row.id}/anular`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ motivo: 'Cliente no pudo pagar' }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'No se pudo anular la venta');
            alert(data.preventaReabierta
                ? `Venta #${row.id} anulada. La preventa #${data.preventaReabierta} quedó pendiente.`
                : `Venta #${row.id} anulada.`);
            fetchData();
        } catch (err) {
            alert(err.message || 'No se pudo anular la venta');
        }
    };

    const formatDateTime = (dateString) => formatFechaHoraLista(dateString) || '-';

    const formatEnvaseLine = (sale) => {
        if (!sale?.envase_tipo) return '';
        const monto = sale.envase_monto || 500;
        if (sale.envase_tipo === 'cargo') return `Cargo envase +${formatCurrency(monto)}`;
        if (sale.envase_tipo === 'devolucion') return `Devolución envase −${formatCurrency(monto)}`;
        return '';
    };

    const fetchSummary = async () => {
        try {
            const res = await fetch(`${API_URL}/api/sales/summary`, { headers });
            if (res.ok) setSummary(await res.json());
        } catch (err) {
            console.error(err);
        }
    };

    const rangeParams = (rango) => new URLSearchParams({ fecha_inicio: rango.desde, fecha_fin: rango.hasta });

    const buscar = async (rango = { desde: fechaDesde, hasta: fechaHasta }) => {
        if (!rango.desde || !rango.hasta) {
            alert('Seleccione la fecha y hora de inicio y de término.');
            return;
        }
        if (rango.desde > rango.hasta) {
            alert('La fecha de inicio no puede ser posterior a la de término.');
            return;
        }
        setSearching(true);
        try {
            const params = rangeParams(rango);
            params.set('limit', '20000');
            const res = await fetch(`${API_URL}/api/sales?${params.toString()}`, { headers });
            const data = await res.json().catch(() => null);
            if (!res.ok) throw new Error(data?.error || 'No se pudieron cargar las ventas');
            setVentas(data);
            setSearched(rango);
        } catch (err) {
            console.error(err);
            alert(err.message || 'No se pudieron cargar las ventas');
        } finally {
            setSearching(false);
        }
    };

    // Refresh the summary and, if there was a search, the same range (after voiding a sale).
    const fetchData = () => {
        fetchSummary();
        if (searched) buscar(searched);
    };

    const exportarExcel = async () => {
        if (!searched) return;
        setExporting(true);
        try {
            const res = await fetch(`${API_URL}/api/sales/export?${rangeParams(searched).toString()}`, { headers });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'No se pudo generar el Excel');
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ventas_${searched.desde.replace(/[^0-9A-Za-z]+/g, '-')}_${searched.hasta.replace(/[^0-9A-Za-z]+/g, '-')}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            alert(err.message || 'No se pudo generar el Excel');
        } finally {
            setExporting(false);
        }
    };



    const fetchVentaDetail = async (id) => {
        setLoadingDetail(true);
        try {
            const res = await fetch(`${API_URL}/api/sales/${id}`, { headers });
            if (res.ok) {
                const data = await res.json();
                setSelectedVenta(data);
                setShowModal(true);
            }
        } catch (err) {
            console.error(err);
            alert('Error al cargar detalle');
        } finally {
            setLoadingDetail(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const roundBoletaClp = (n) => {
        const x = Math.round(Number(n) || 0);
        return Math.max(0, Math.round(x / 10) * 10);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('es-CL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDateShort = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-CL');
    };

    const tipoPagoLabel = (tipo) => {
        switch (tipo) {
            case 'efectivo': return 'Efectivo';
            case 'tarjeta_debito': return 'Tarjeta débito';
            case 'tarjeta_credito': return 'Tarjeta crédito';
            case 'transferencia': return 'Transferencia';
            default: return tipo || '—';
        }
    };

    const parsePagosRow = (row) => {
        const p = row?.pagos;
        if (!p) return [];
        if (Array.isArray(p)) return p;
        if (typeof p === 'string') {
            try {
                const j = JSON.parse(p);
                return Array.isArray(j) ? j : [];
            } catch {
                return [];
            }
        }
        return [];
    };

    const formatPagosResumen = (row) => {
        const arr = parsePagosRow(row);
        if (arr.length === 0) return 'Sin registro';
        return arr.map((x) => `${tipoPagoLabel(x.tipo_pago)} ${formatCurrency(x.monto)}`).join(' · ');
    };

    // Totals of the searched range. Voided sales are listed but do not add up.
    const totales = useMemo(() => {
        const t = { validas: 0, anuladas: 0, total: 0, neto: 0, iva: 0 };
        for (const v of ventas) {
            if (v.estado === false || v.estado === 'false') {
                t.anuladas += 1;
                continue;
            }
            t.validas += 1;
            t.total += Number(v.sub_total) || 0;
            t.neto += Number(v.monto_neto) || 0;
            t.iva += Number(v.iva) || 0;
        }
        return t;
    }, [ventas]);

    const iconForTipoPago = (tipo) => {
        if (tipo === 'tarjeta_debito' || tipo === 'tarjeta_credito') return <CreditCard size={14} />;
        return <Banknote size={14} />;
    };

    const printSalesDetail = async () => {
        const dias = daysInRange(searched?.desde, searched?.hasta);
        if (dias > MAX_PRINT_DAYS) {
            alert(`El rango no puede superar ${MAX_PRINT_DAYS} días.`);
            return;
        }
        if (!ventas.length) {
            alert('No hay ventas en la lista para imprimir.');
            return;
        }
        setPrintingDay(true);
        try {
            const details = await Promise.all(
                ventas.map(async (v) => {
                    const res = await fetch(`${API_URL}/api/sales/${v.id}`, { headers });
                    if (!res.ok) return null;
                    return res.json();
                })
            );
            const valid = details.filter(Boolean);
            const rangoLabel = `${formatFiltroLabel(searched?.desde)} — ${formatFiltroLabel(searched?.hasta)}`;
            const blocks = valid.map((sale) => {
                const pagos = parsePagosRow(sale);
                const pagosHtml = pagos.length === 0
                    ? '<p class="muted">Sin registro de medios de pago</p>'
                    : `<ul>${pagos.map((p) => `<li><strong>${tipoPagoLabel(p.tipo_pago)}</strong>: ${formatCurrency(p.monto)}</li>`).join('')}</ul>`;
                const envaseLine = formatEnvaseLine(sale);
                const itemsRows = (sale.items || []).map((item, idx) =>
                    `<tr>
                        <td>${idx + 1}</td>
                        <td>${(item.codigo_barra || '—').replace(/</g, '')}</td>
                        <td>${String(item.producto_nombre || '').replace(/</g, '')}</td>
                        <td style="text-align:center">${item.cantidad}</td>
                        <td style="text-align:right">${formatCurrency(item.precio_unitario)}</td>
                        <td style="text-align:right">${formatCurrency(item.precio_total)}</td>
                    </tr>`
                ).join('');
                const totalR = roundBoletaClp(sale.sub_total);
                const raw = Math.round(Number(sale.sub_total) || 0);
                return `
                    <section class="sale-block">
                        <h3>Venta #${sale.id} — ${formatDateTime(sale.created_at || sale.fecha)}</h3>
                        <p class="meta">Vendedor: ${String(displayVendedor(sale)).replace(/</g, '')} · Cajero: ${String(sale.cajero_name || sale.vendedor_name || '—').replace(/</g, '')} · Caja: ${String(sale.caja_nombre || '—').replace(/</g, '')}${envaseLine ? ` · ${envaseLine}` : ''}</p>
                        <div class="pagos"><strong>Medios de pago</strong>${pagosHtml}</div>
                        <table>
                            <thead><tr><th>#</th><th>Código</th><th>Producto</th><th>Cant.</th><th>P. unit.</th><th>Total</th></tr></thead>
                            <tbody>${itemsRows || '<tr><td colspan="6">Sin líneas</td></tr>'}</tbody>
                        </table>
                        <p class="total-line">Total (redondeado boleta): <strong>${formatCurrency(totalR)}</strong>${totalR !== raw ? ` <span class="muted">(registrado: ${formatCurrency(sale.sub_total)})</span>` : ''}</p>
                    </section>
                `;
            });
            const blocksHtml = blocks.join('');

            const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Detalle ventas del día</title>
            <style>
              body { font-family: system-ui, sans-serif; margin: 12mm; font-size: 11px; color: #111; }
              h1 { font-size: 1.25rem; margin: 0 0 8px 0; }
              .sub { color: #555; margin-bottom: 20px; }
              .sale-block { page-break-inside: avoid; margin-bottom: 28px; border-bottom: 1px solid #ddd; padding-bottom: 16px; }
              .sale-block h3 { margin: 0 0 6px 0; font-size: 1rem; }
              .meta { margin: 0 0 8px 0; color: #444; }
              .pagos ul { margin: 4px 0 10px 16px; padding: 0; }
              table { width: 100%; border-collapse: collapse; margin-top: 6px; }
              th, td { border: 1px solid #ccc; padding: 4px 6px; }
              th { background: #f3f4f6; text-align: left; font-size: 10px; }
              .total-line { margin-top: 8px; font-size: 12px; }
              .muted { color: #666; font-weight: normal; }
            </style></head><body>
            <h1>Historial — detalle de ventas</h1>
            <p class="sub">${rangoLabel} · ${valid.length} venta(s)</p>
            ${blocksHtml}
            <script>window.onload=function(){window.print();setTimeout(function(){window.close();},400);};</script>
            </body></html>`;

            const w = window.open('', '_blank');
            if (!w) {
                alert('Permita ventanas emergentes para imprimir.');
                return;
            }
            w.document.write(html);
            w.document.close();
        } catch (e) {
            console.error(e);
            alert('Error al preparar la impresión');
        } finally {
            setPrintingDay(false);
        }
    };

    const { sorted: sortedVentaItems, thProps: ventaItemsThProps } = useTableSort(selectedVenta?.items || [], {
        getters: {
            cantidad: (item) => Number(item.cantidad) || 0,
            precio_unitario: (item) => Number(item.precio_unitario) || 0,
            precio_total: (item) => Number(item.precio_total) || 0,
        },
    });

    const columns = [
        {
            name: '#',
            selector: row => row.id,
            sortable: true,
            width: '80px',
            cell: row => (
                <span style={{ 
                    fontWeight: '600', 
                    color: ventaAnulada(row) ? '#b91c1c' : 'var(--primary-color)',
                    fontFamily: 'monospace',
                    textDecoration: ventaAnulada(row) ? 'line-through' : 'none'
                }}>
                    #{row.id}{ventaAnulada(row) ? ' anulada' : ''}
                </span>
            )
        },
        {
            name: 'Fecha / Hora',
            selector: row => row.created_at || row.fecha,
            sortable: true,
            minWidth: '158px',
            grow: 0,
            wrap: false,
            cell: row => (
                <span style={{
                    fontVariantNumeric: 'tabular-nums',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    fontSize: '0.82rem',
                    whiteSpace: 'nowrap',
                    letterSpacing: '-0.01em',
                }}>
                    {formatDateTime(row.created_at || row.fecha)}
                </span>
            )
        },
        {
            name: 'Vendedor',
            selector: row => displayVendedor(row),
            sortable: true,
            cell: row => displayVendedor(row)
        },
        {
            name: 'Items',
            selector: row => row.cantidad_productos,
            sortable: true,
            width: '90px',
            cell: row => (
                <span style={{
                    background: 'rgba(79, 70, 229, 0.1)',
                    color: 'var(--primary-color)',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '9999px',
                    fontSize: '0.8rem',
                    fontWeight: '600'
                }}>
                    {row.cantidad_productos}
                </span>
            )
        },
        {
            name: 'Tipo Doc.',
            selector: row => row.tipo_documento,
            sortable: true,
            width: '100px',
            cell: row => (
                <span style={{
                    background: row.tipo_documento === 1 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(79, 70, 229, 0.1)',
                    color: row.tipo_documento === 1 ? 'var(--secondary-color)' : 'var(--primary-color)',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                }}>
                    {row.tipo_documento === 1 ? 'Boleta' : 'Factura'}
                </span>
            )
        },
        {
            name: 'Neto',
            selector: row => row.monto_neto,
            sortable: true,
            cell: row => (
                <span style={{ color: 'var(--text-secondary)', display: 'block', textAlign: 'right' }}>
                    {formatCurrency(row.monto_neto)}
                </span>
            )
        },
        {
            name: 'IVA',
            selector: row => row.iva,
            sortable: true,
            cell: row => (
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', display: 'block', textAlign: 'right' }}>
                    {formatCurrency(row.iva)}
                </span>
            )
        },
        {
            name: 'Medios de pago',
            selector: row => formatPagosResumen(row),
            sortable: true,
            minWidth: '200px',
            cell: row => (
                <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                    {formatPagosResumen(row)}
                </span>
            )
        },
        {
            name: 'Total',
            selector: row => row.sub_total,
            sortable: true,
            cell: row => (
                <span style={{ 
                    fontWeight: '700', 
                    color: 'var(--secondary-color)',
                    fontSize: '1rem',
                    display: 'block',
                    textAlign: 'right'
                }}>
                    {formatCurrency(row.sub_total)}
                </span>
            )
        },
        {
            name: '',
            width: '110px',
            cell: row => (
                <div style={{ display: 'flex', gap: '0.35rem' }}>
                    <button
                        onClick={() => fetchVentaDetail(row.id)}
                        disabled={loadingDetail}
                        style={{
                            background: 'var(--primary-color)',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.5rem',
                            cursor: 'pointer',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        title="Ver Detalle"
                    >
                        <Eye size={16} />
                    </button>
                    {!ventaAnulada(row) && (
                        <button
                            type="button"
                            onClick={() => anularVenta(row)}
                            style={{
                                background: '#fee2e2',
                                border: '1px solid #fecaca',
                                borderRadius: '8px',
                                padding: '0.5rem',
                                cursor: 'pointer',
                                color: '#b91c1c',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            title="Anular venta (cliente no pagó)"
                        >
                            <Ban size={16} />
                        </button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
        }
    ];

    const searchBtnStyle = (disabled) => ({
        padding: '0.5rem 1rem',
        borderRadius: '8px',
        border: '1px solid var(--border-color)',
        background: 'var(--bg-input)',
        color: 'var(--text-main)',
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: '0.875rem',
        opacity: disabled ? 0.55 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
    });

    return (
        <div className="page">
            {/* Header */}
            <div style={{ marginBottom: '1.5rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                <div>
                    <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                        <Receipt size={28} />
                        Historial de Ventas
                    </h1>
                    <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                        {searched
                            ? `${ventas.length} venta${ventas.length === 1 ? '' : 's'} entre ${formatFiltroLabel(searched.desde)} y ${formatFiltroLabel(searched.hasta)}`
                            : 'Elija fecha y hora y presione Buscar para ver las ventas'}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        Desde
                        <input
                            type="datetime-local"
                            step="60"
                            value={fechaDesde}
                            onChange={(e) => setFechaDesde(e.target.value)}
                            title="Fecha y hora de Chile"
                            style={{ marginLeft: '0.35rem', padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-main)' }}
                        />
                    </label>
                    <label style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        Hasta
                        <input
                            type="datetime-local"
                            step="60"
                            value={fechaHasta}
                            onChange={(e) => setFechaHasta(e.target.value)}
                            title="Fecha y hora de Chile"
                            style={{ marginLeft: '0.35rem', padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-main)' }}
                        />
                    </label>
                    <button
                        type="button"
                        onClick={() => {
                            const hoy = rangoHoyChile();
                            setFechaDesde(hoy.desde);
                            setFechaHasta(hoy.hasta);
                        }}
                        style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)',
                            background: 'transparent',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                        }}
                    >
                        Hoy
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => buscar()}
                        disabled={searching}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                    >
                        {searching ? <Loader2 size={16} className="spinning" /> : <Search size={16} />}
                        Buscar
                    </button>
                    <button
                        type="button"
                        onClick={exportarExcel}
                        disabled={!searched || exporting || searching}
                        style={searchBtnStyle(!searched || exporting || searching)}
                        title={searched ? 'Descarga en Excel las ventas del rango buscado' : 'Primero busque un rango'}
                    >
                        {exporting ? <Loader2 size={16} className="spinning" /> : <FileSpreadsheet size={16} />}
                        Exportar Excel
                    </button>
                    <button
                        type="button"
                        onClick={printSalesDetail}
                        disabled={printingDay || ventas.length === 0 || daysInRange(searched?.desde, searched?.hasta) > MAX_PRINT_DAYS}
                        style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)',
                            background: 'var(--bg-input)',
                            color: 'var(--text-main)',
                            fontWeight: 600,
                            cursor: printingDay || ventas.length === 0 || daysInRange(searched?.desde, searched?.hasta) > MAX_PRINT_DAYS ? 'not-allowed' : 'pointer',
                            fontSize: '0.875rem',
                            opacity: printingDay || ventas.length === 0 || daysInRange(searched?.desde, searched?.hasta) > MAX_PRINT_DAYS ? 0.55 : 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.35rem'
                        }}
                        title={daysInRange(searched?.desde, searched?.hasta) > MAX_PRINT_DAYS ? `Máximo ${MAX_PRINT_DAYS} días` : 'Imprime cada venta con líneas y medios de pago'}
                    >
                        {printingDay ? <Loader2 size={16} className="spinning" /> : <Printer size={16} />}
                        Imprimir detalle
                    </button>
                </div>
            </div>

            {/* Totals of the searched range */}
            {searched && (
                <div className="report-card" style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', alignItems: 'flex-end' }}>
                        <div>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>
                                Total ventas · {formatFiltroLabel(searched.desde)} a {formatFiltroLabel(searched.hasta)}
                            </p>
                            <p style={{ fontSize: '2rem', fontWeight: 700, margin: 0, color: 'var(--secondary-color)' }}>
                                {formatCurrency(totales.total)}
                            </p>
                        </div>
                        <div>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>N° de ventas</p>
                            <p style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>{totales.validas}</p>
                        </div>
                        <div>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>Neto</p>
                            <p style={{ fontSize: '1.15rem', fontWeight: 600, margin: 0 }}>{formatCurrency(totales.neto)}</p>
                        </div>
                        <div>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>IVA</p>
                            <p style={{ fontSize: '1.15rem', fontWeight: 600, margin: 0 }}>{formatCurrency(totales.iva)}</p>
                        </div>
                        {totales.anuladas > 0 && (
                            <div>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>Anuladas (no suman)</p>
                                <p style={{ fontSize: '1.15rem', fontWeight: 600, margin: 0, color: '#b91c1c' }}>{totales.anuladas}</p>
                            </div>
                        )}
                    </div>
                    {ventas.length >= 20000 && (
                        <p style={{ color: '#b45309', fontSize: '0.85rem', margin: '0.75rem 0 0' }}>
                            Se alcanzó el máximo de 20.000 ventas; acote el rango para ver todo.
                        </p>
                    )}
                </div>
            )}

            {/* Summary Cards */}
            {summary && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div className="report-card" style={{ padding: '1.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{ 
                                background: 'rgba(79, 70, 229, 0.1)', 
                                borderRadius: '12px', 
                                padding: '0.75rem',
                                display: 'flex'
                            }}>
                                <ShoppingBag size={24} style={{ color: 'var(--primary-color)' }} />
                            </div>
                            <div>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>Ventas Hoy</p>
                                <p style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>{summary.ventas_hoy || 0}</p>
                            </div>
                        </div>
                    </div>
                    <div className="report-card" style={{ padding: '1.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{ 
                                background: 'rgba(16, 185, 129, 0.1)', 
                                borderRadius: '12px', 
                                padding: '0.75rem',
                                display: 'flex'
                            }}>
                                <DollarSign size={24} style={{ color: 'var(--secondary-color)' }} />
                            </div>
                            <div>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>Total Hoy</p>
                                <p style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0, color: 'var(--secondary-color)' }}>
                                    {formatCurrency(summary.total_hoy)}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="report-card" style={{ padding: '1.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{ 
                                background: 'rgba(245, 158, 11, 0.1)', 
                                borderRadius: '12px', 
                                padding: '0.75rem',
                                display: 'flex'
                            }}>
                                <TrendingUp size={24} style={{ color: 'var(--accent-color)' }} />
                            </div>
                            <div>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>Ventas Mes</p>
                                <p style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>{summary.ventas_mes || 0}</p>
                            </div>
                        </div>
                    </div>
                    <div className="report-card" style={{ padding: '1.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{ 
                                background: 'rgba(236, 72, 153, 0.1)', 
                                borderRadius: '12px', 
                                padding: '0.75rem',
                                display: 'flex'
                            }}>
                                <DollarSign size={24} style={{ color: '#EC4899' }} />
                            </div>
                            <div>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>Total Mes</p>
                                <p style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0, color: '#EC4899' }}>
                                    {formatCurrency(summary.total_mes)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="report-card" style={{ padding: 0, overflow: 'hidden' }}>
                <DataTable
                    columns={columns}
                    data={ventas}
                    progressPending={searching}
                    progressComponent={<Loader2 size={32} className="spinning" style={{ color: 'var(--primary-color)', margin: '2rem' }} />}
                    pagination
                    paginationPerPage={50}
                    paginationRowsPerPageOptions={[20, 50, 100, 200, 500]}
                    paginationComponentOptions={{
                        rowsPerPageText: 'Filas por página:',
                        rangeSeparatorText: 'de'
                    }}
                    highlightOnHover
                    responsive
                    noDataComponent={
                        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                            <Receipt size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                            <p>{searched ? 'No hay ventas en el rango seleccionado' : 'Seleccione un rango de fecha y hora y presione Buscar'}</p>
                        </div>
                    }
                    customStyles={{
                        headRow: {
                            style: {
                                backgroundColor: '#f9fafb',
                                borderBottom: '1px solid var(--border-color)',
                                fontWeight: '600',
                                color: 'var(--text-secondary)',
                                textTransform: 'uppercase',
                                fontSize: '0.7rem',
                                letterSpacing: '0.05em'
                            }
                        },
                        cells: {
                            style: {
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            }
                        }
                    }}
                />
            </div>

            {/* Detail Modal */}
            {showModal && selectedVenta && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
                        <button 
                            className="modal-close" 
                            onClick={() => { setShowModal(false); setSelectedVenta(null); }}
                        >
                            <X size={20} />
                        </button>
                        
                        {/* Header */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h2 style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.5rem',
                                marginBottom: '0.5rem'
                            }}>
                                <Receipt size={24} />
                                Venta #{selectedVenta.id}
                            </h2>
                            <div style={{ 
                                display: 'flex', 
                                gap: '1rem', 
                                color: 'var(--text-secondary)',
                                fontSize: '0.875rem',
                                flexWrap: 'wrap'
                            }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                    <Calendar size={14} /> {formatDateTime(selectedVenta.created_at || selectedVenta.fecha)}
                                </span>
                                <span>Vendedor: <strong>{displayVendedor(selectedVenta)}</strong></span>
                                <span>Cajero: <strong>{selectedVenta.cajero_name || selectedVenta.vendedor_name || '-'}</strong></span>
                                <span>Caja: <strong>{selectedVenta.caja_nombre || '-'}</strong></span>
                                {formatEnvaseLine(selectedVenta) && (
                                    <span><strong>{formatEnvaseLine(selectedVenta)}</strong></span>
                                )}
                                {selectedVenta.preventa_id && (
                                    <span style={{ 
                                        background: 'rgba(79, 70, 229, 0.1)', 
                                        color: 'var(--primary-color)',
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '4px',
                                        fontSize: '0.75rem'
                                    }}>
                                        Preventa #{selectedVenta.preventa_id}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Summary */}
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                            gap: '1rem',
                            padding: '1rem',
                            background: 'var(--bg-input)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: '1.5rem'
                        }}>
                            <div>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', margin: '0 0 0.25rem 0' }}>Subtotal Neto</p>
                                <p style={{ fontWeight: '600', margin: 0 }}>{formatCurrency(selectedVenta.monto_neto)}</p>
                            </div>
                            <div>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', margin: '0 0 0.25rem 0' }}>IVA (19%)</p>
                                <p style={{ fontWeight: '600', margin: 0 }}>{formatCurrency(selectedVenta.iva)}</p>
                            </div>
                            <div>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', margin: '0 0 0.25rem 0' }}>Total</p>
                                <p style={{ fontWeight: '700', margin: 0, color: 'var(--secondary-color)', fontSize: '1.25rem' }}>
                                    {formatCurrency(selectedVenta.sub_total)}
                                </p>
                            </div>
                            <div>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', margin: '0 0 0.25rem 0' }}>Tipo Documento</p>
                                <p style={{ fontWeight: '600', margin: 0 }}>
                                    {selectedVenta.tipo_documento === 1 ? 'Boleta' : 'Factura'}
                                </p>
                            </div>
                            <div>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', margin: '0 0 0.25rem 0' }}>Items</p>
                                <p style={{ fontWeight: '600', margin: 0 }}>
                                    {selectedVenta.cantidad_productos} productos
                                </p>
                            </div>
                        </div>

                        {/* Pagos */}
                        <div style={{
                            padding: '1rem',
                            background: 'rgba(79, 70, 229, 0.06)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: '1.5rem',
                            border: '1px solid var(--border-color)'
                        }}>
                            <h3 style={{
                                fontSize: '0.95rem',
                                fontWeight: '600',
                                margin: '0 0 0.75rem 0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <DollarSign size={18} />
                                Medios de pago
                            </h3>
                            {parsePagosRow(selectedVenta).length === 0 ? (
                                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                                    Sin registro de pagos en sistema.
                                </p>
                            ) : (
                                <ul style={{
                                    listStyle: 'none',
                                    margin: 0,
                                    padding: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '0.5rem'
                                }}>
                                    {parsePagosRow(selectedVenta).map((p, i) => (
                                        <li
                                            key={`${p.tipo_pago}-${i}`}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: '0.75rem'
                                            }}
                                        >
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.875rem' }}>
                                                {iconForTipoPago(p.tipo_pago)}
                                                {tipoPagoLabel(p.tipo_pago)}
                                            </span>
                                            <strong>{formatCurrency(p.monto)}</strong>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Items Detail Table */}
                        <h3 style={{ 
                            fontSize: '1rem', 
                            fontWeight: '600', 
                            marginBottom: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}>
                            <Hash size={18} />
                            Detalle de Productos ({selectedVenta.items?.length || 0} items)
                        </h3>

                        <div style={{ 
                            border: '1px solid var(--border-color)', 
                            borderRadius: 'var(--radius-md)',
                            overflow: 'hidden'
                        }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ 
                                        background: 'var(--bg-input)', 
                                        fontSize: '0.7rem',
                                        textTransform: 'uppercase',
                                        color: 'var(--text-secondary)',
                                        fontWeight: '600',
                                        letterSpacing: '0.05em'
                                    }}>
                                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', width: '80px' }}>#</th>
                                        <SortableTh columnKey="codigo_barra" {...ventaItemsThProps} style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Código</SortableTh>
                                        <SortableTh columnKey="producto_nombre" {...ventaItemsThProps} style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Producto</SortableTh>
                                        <SortableTh columnKey="cantidad" {...ventaItemsThProps} style={{ padding: '0.75rem 1rem', textAlign: 'center', width: '80px' }} align="center">Cant.</SortableTh>
                                        <SortableTh columnKey="precio_unitario" {...ventaItemsThProps} style={{ padding: '0.75rem 1rem', textAlign: 'right', width: '120px' }} align="right">P. Unitario</SortableTh>
                                        <SortableTh columnKey="precio_total" {...ventaItemsThProps} style={{ padding: '0.75rem 1rem', textAlign: 'right', width: '120px' }} align="right">Total</SortableTh>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedVentaItems.map((item, idx) => (
                                        <tr 
                                            key={item.detalle_id || idx}
                                            style={{ 
                                                borderBottom: idx < selectedVenta.items.length - 1 ? '1px solid var(--border-color)' : 'none'
                                            }}
                                        >
                                            <td style={{ 
                                                padding: '0.75rem 1rem', 
                                                color: 'var(--text-secondary)',
                                                fontSize: '0.875rem'
                                            }}>
                                                {idx + 1}
                                            </td>
                                            <td style={{ 
                                                padding: '0.75rem 1rem',
                                                fontFamily: 'monospace',
                                                fontSize: '0.8rem',
                                                color: 'var(--text-secondary)'
                                            }}>
                                                {item.codigo_barra || '-'}
                                            </td>
                                            <td style={{ padding: '0.75rem 1rem' }}>
                                                <div style={{ fontWeight: '500' }}>{item.producto_nombre || 'Producto'}</div>
                                                {item.producto_descripcion && (
                                                    <div style={{ 
                                                        fontSize: '0.75rem', 
                                                        color: 'var(--text-secondary)',
                                                        marginTop: '0.25rem'
                                                    }}>
                                                        {item.producto_descripcion.substring(0, 50)}...
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ 
                                                padding: '0.75rem 1rem', 
                                                textAlign: 'center',
                                                fontWeight: '600'
                                            }}>
                                                <span style={{
                                                    background: 'rgba(79, 70, 229, 0.1)',
                                                    color: 'var(--primary-color)',
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '9999px',
                                                    fontSize: '0.875rem'
                                                }}>
                                                    {item.cantidad}
                                                </span>
                                            </td>
                                            <td style={{ 
                                                padding: '0.75rem 1rem', 
                                                textAlign: 'right',
                                                color: 'var(--text-secondary)'
                                            }}>
                                                {formatCurrency(item.precio_unitario)}
                                            </td>
                                            <td style={{ 
                                                padding: '0.75rem 1rem', 
                                                textAlign: 'right',
                                                fontWeight: '700',
                                                color: 'var(--text-main)'
                                            }}>
                                                {formatCurrency(item.precio_total)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr style={{ 
                                        background: 'var(--bg-input)', 
                                        fontWeight: '700',
                                        borderTop: '2px solid var(--border-color)'
                                    }}>
                                        <td colSpan="3" style={{ padding: '1rem' }}></td>
                                        <td style={{ 
                                            padding: '1rem', 
                                            textAlign: 'center',
                                            color: 'var(--text-secondary)'
                                        }}>
                                            {selectedVenta.items?.reduce((sum, item) => sum + item.cantidad, 0)} items
                                        </td>
                                        <td style={{ 
                                            padding: '1rem', 
                                            textAlign: 'right',
                                            color: 'var(--text-secondary)'
                                        }}>
                                            Total:
                                        </td>
                                        <td style={{ 
                                            padding: '1rem', 
                                            textAlign: 'right',
                                            fontSize: '1.125rem',
                                            color: 'var(--secondary-color)'
                                        }}>
                                            {formatCurrency(selectedVenta.sub_total)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {/* Footer */}
                        <div style={{ 
                            marginTop: '1.5rem', 
                            display: 'flex', 
                            justifyContent: 'flex-end' 
                        }}>
                            <button 
                                onClick={() => { setShowModal(false); setSelectedVenta(null); }}
                                className="btn-primary"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
